import { useEffect, useMemo, useState } from "react";
import { NativeSelect, TextInput } from "@mantine/core";
import { callFunction } from "../api/client";
import type { ImportField, ImportValue, Job } from "../api/types";
import { useListParams } from "../lib/useListParams";
import {
  CONFIDENCE_WORDS, KIND_WORDS, MARKS, MARK_FIELDS, OUTCOME_ORDER, OUTCOME_WORDS, ROLE_WORDS, SOURCE_WORDS,
  mappingFrom, readData, type Constant, type LookRow, type Proposal,
} from "../lib/importRead";
import { Badge } from "../ui/Badge";
import { Button } from "../ui/Button";
import { DataTable, type ColumnSpec } from "../ui/DataTable";
import { DownloadJobFile } from "./DownloadJobFile";

/* What EVIE made of an uploaded file, for approval before it is loaded.
 *
 * The engine read the file (ingestion/csv_read.py): it placed every column
 * it could, asked its model about the rest, ran the whole file through the
 * front door and rolled it back. This shows that reading in the order a
 * person decides in: what the file is, what loading it would do, the rows
 * to look at, and then the columns, folded unless something in them needs
 * a look. Load it is the one step. A column's meaning can be changed and
 * the file read again, which is the rare path.
 *
 * James, 2026-10-13: automated by default, approval before it writes, and
 * no column by column step for everything. docs/ux-decisions.md, Import. */

const LOOK_CAP = 200;

/** Where a column's reading came from, how sure EVIE was, and its reason, as
 *  one line. A header the engine knows has its reason as the whole line,
 *  because the source word would say the same thing twice. */
function whyWords(p: Proposal): string {
  const lead = p.source === "known" ? null : (SOURCE_WORDS[p.source] ?? p.source);
  const sure = p.confidence ? (CONFIDENCE_WORDS[p.confidence] ?? p.confidence).toLowerCase() : null;
  const parts = [lead && sure ? `${lead}, ${sure}` : lead, p.reason].filter((x): x is string => Boolean(x));
  return parts.length ? parts.join(". ") : (SOURCE_WORDS[p.source] ?? p.source);
}

export function ImportPreview({ token, job, uploadKey, fields, source: source0, asOf: asOf0, marks, choices, onMark,
  onReread, onLoaded }: {
  token: string;
  job: Job;
  uploadKey: string;
  fields: readonly ImportField[];
  source: string;
  asOf: string;
  /** The values for every row, set on the form above (James, 2026-10-23).
   *  Load it sends them as the mapping's added values. */
  marks: Record<string, string>;
  choices: readonly ImportValue[];
  onMark: (field: string, value: string) => void;
  /** A read with the columns changed was queued. */
  onReread: (jobId: string, source: string, asOf: string) => void;
  /** The import was queued. */
  onLoaded: (jobId: string) => void;
}) {
  const read = readData(job);
  const [targets, setTargets] = useState<Record<string, string>>(
    () => Object.fromEntries((read?.proposal ?? []).map((p) => [p.header, p.target])));
  const [relationship, setRelationship] = useState<string | null>(read?.relationship ?? null);
  // The values the engine added to every row, such as a state from the file's
  // name, apart from the marks, which are the form's.
  const [constants, setConstants] = useState<Constant[]>(
    () => (read?.constants ?? []).filter((c) => !MARK_FIELDS.has(c.field)));
  const [source, setSource] = useState(source0);
  const [asOf, setAsOf] = useState(asOf0);
  const [busy, setBusy] = useState<"load" | "reread" | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  // Two tables on one page, each with its own place in the URL.
  const columnParams = useListParams("header", [], true, "", "ic.");
  const lookParams = useListParams("row", [], true, "", "ir.");

  const needsALook = useMemo(() => (read?.proposal ?? []).some(
    (p) => p.source === "none" || p.confidence === "low" || (p.target === "note" && p.source !== "person")), [read]);
  const [columnsOpen, setColumnsOpen] = useState(needsALook);
  // A mark a saved mapping for this file's shape brought is set on the form,
  // where the person can see it and change it, unless the form already has
  // one for that field.
  useEffect(() => {
    for (const c of read?.constants ?? []) {
      if (MARK_FIELDS.has(c.field) && !marks[c.field]) onMark(c.field, c.value);
    }
    // Once, for this reading of the file.
  }, [job.id]);

  if (!read) return null;

  const edited = (read.proposal.some((p) => targets[p.header] !== p.target)
    || relationship !== read.relationship
    || constants.length !== read.constants.filter((c) => !MARK_FIELDS.has(c.field)).length);
  const hasCompanyAndProperty = Object.values(targets).some((t) => t.startsWith("company") || t === "split:address:company" || t === "split:city_state:company")
    && Object.values(targets).some((t) => t.startsWith("property") || t === "split:address" || t === "split:city_state:property");
  const loadable = Object.values(targets).some((t) => t !== "note" && t !== "skip");
  const marked: Constant[] = MARKS.filter((m) => marks[m.field])
    .map((m) => ({ field: m.field, value: marks[m.field] ?? "", reason: "Chosen by a person.", source: "person" }));

  const wordFor = (field: string, value: string) =>
    choices.find((c) => c.target === field && c.value === value)?.label ?? value;
  const labelFor = (field: string) => MARKS.find((m) => m.field === field)?.label ?? field;
  // EVIE's suggestions for a mark the form has not set, each with Use it.
  const suggestions = read.suggested.filter((s) => !marks[s.field] && choices.some((c) => c.target === s.field));

  const mapping = () => mappingFrom(targets, hasCompanyAndProperty ? (relationship ?? "none") : relationship,
    [...constants, ...marked], read);

  async function reread() {
    setBusy("reread"); setSaid(null);
    try {
      const id = await callFunction<string>(token, "read_csv", {
        p_key: uploadKey, p_source: source, p_as_of: asOf || null, p_mapping: mapping(),
      });
      onReread(id, source, asOf);
    } catch (e) {
      setSaid(e instanceof Error ? e.message : String(e));
      setBusy(null);
    }
  }

  async function load() {
    setBusy("load"); setSaid(null);
    try {
      const id = await callFunction<string>(token, "load_csv", {
        p_key: uploadKey, p_headers: read?.proposal.map((p) => p.header) ?? [],
        p_mapping: mapping(), p_source: source, p_as_of: asOf || null,
      });
      onLoaded(id);
    } catch (e) {
      setSaid(e instanceof Error ? e.message : String(e));
      setBusy(null);
    }
  }

  const selectData = selectOptions(fields);

  const columnSpecs: ColumnSpec<Proposal>[] = [
    { id: "header", header: "Column", role: "leading", fields: [], title: true, narrowLabel: null,
      cell: (p) => p.header },
    { id: "samples", header: "Sample values", role: "secondary", fields: [], narrowLabel: "Values",
      cell: (p) => <span className="muted ip-samples">{p.samples.length ? p.samples.join(" | ") : "none"}</span> },
    { id: "target", header: "Read as", role: "leading", fields: [], narrowLabel: null,
      cell: (p) => (
        <NativeSelect
          aria-label={`${p.header}, read as`}
          size="xs"
          value={targets[p.header] ?? p.target}
          data={selectData}
          onChange={(e) => setTargets({ ...targets, [p.header]: e.currentTarget.value })}
        />
      ) },
    { id: "why", header: "Why", role: "secondary", fields: [], narrowLabel: null,
      cell: (p) => <span className="muted">{whyWords(p)}</span> },
    { id: "becomes", header: "Becomes", role: "detail", fields: [],
      cell: (p) => <span className="muted ip-samples">{p.becomes.join(" | ")}</span> },
  ];

  const lookSpecs: ColumnSpec<LookRow>[] = [
    { id: "row", header: "Row", role: "secondary", fields: [], align: "num", cell: (r) => r.row.toLocaleString() },
    { id: "record", header: "Record", role: "leading", fields: [], title: true, narrowLabel: null,
      cell: (r) => Object.values(r.names).join(", ") || "no record on the row" },
    { id: "result", header: "Result", role: "leading", fields: [], narrowLabel: null,
      cell: (r) => (
        <span className="ip-results">
          {r.what.map((w, i) => (
            <Badge key={i} tone={w.result === "held for review" ? "waiting" : w.result === "skipped" ? "quiet" : "refusal"}>
              {w.kind === "row" ? w.result : `${KIND_WORDS[w.kind]?.toLowerCase() ?? w.kind}: ${w.result}`}
            </Badge>
          ))}
        </span>
      ) },
    { id: "why", header: "Why", role: "secondary", fields: [], narrowLabel: null,
      cell: (r) => <span className="muted">{r.what.map((w) => w.why).filter(Boolean).join(". ")}</span> },
  ];

  const kindsPresent = Object.keys(read.totals);
  /* The preview rehearses the first rows a real load commits at once, and
     says so when the file has more (engine csv_read.rehearse). */
  const sampled = read.rehearsed !== null && read.rehearsed < read.rows ? read.rehearsed : null;

  return (
    <section className="ip" aria-label="Preview">
      <h2>Preview</h2>
      <p className="ip-says">{read.says}</p>
      {read.sheetNote ? <p className="muted">{read.sheetNote}</p> : null}
      {read.model.whyNot && read.model.unplaced > 0 ? (
        <p className="muted">EVIE could not read {read.model.unplaced} {read.model.unplaced === 1 ? "column" : "columns"} it did not know: {read.model.whyNot} They are kept as notes.</p>
      ) : null}

      {sampled ? <p className="muted">From the first {sampled.toLocaleString()} of {read.rows.toLocaleString()} rows.</p> : null}
      {read.preview && kindsPresent.length ? (
        <table className="ip-totals">
          <thead>
            <tr><th>Loading it would make</th>{OUTCOME_ORDER.map((o) => <th key={o} className="num">{OUTCOME_WORDS[o]}</th>)}</tr>
          </thead>
          <tbody>
            {kindsPresent.map((kind) => (
              <tr key={kind}>
                <td>{kind}</td>
                {OUTCOME_ORDER.map((o) => <td key={o} className="num">{(read.totals[kind]?.[o] ?? 0).toLocaleString()}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
      {read.failed ? (
        <p className="refusal" role="alert">
          {read.failed.toLocaleString()} {sampled ? `of the first ${sampled.toLocaleString()} rows` : read.failed === 1 ? "row" : "rows"} could not be read. The rows to look at say why.
        </p>
      ) : null}

      {suggestions.map((s) => (
        <p key={s.field} className="ip-suggest">
          EVIE suggests {labelFor(s.field)}: {wordFor(s.field, s.value)}. {s.reason}{" "}
          <Button kind="link" size="compact-sm" onClick={() => onMark(s.field, s.value)}>Use it</Button>
        </p>
      ))}
      {hasCompanyAndProperty ? (
        <NativeSelect
          label="What the company is to the property"
          description={read.relationshipReason ?? undefined}
          className="ip-role"
          value={relationship ?? "none"}
          data={Object.entries(ROLE_WORDS).map(([value, label]) => ({ value, label }))}
          onChange={(e) => setRelationship(e.currentTarget.value)}
        />
      ) : null}
      {marked.length ? (
        <p className="muted ip-marked">
          Values for every row: {marked.map((m) => `${labelFor(m.field)} ${wordFor(m.field, m.value)}`).join(", ")}. Change them above.
        </p>
      ) : null}
      <div className="ip-load bar">
        <Button kind="orange" onClick={() => void load()} disabled={!loadable || busy !== null} loading={busy === "load"}>Load it</Button>
        {sampled ? <span className="muted">Load it reads all {read.rows.toLocaleString()} rows.</span> : null}
        {edited ? (
          <Button onClick={() => void reread()} disabled={busy !== null} loading={busy === "reread"}>Check again</Button>
        ) : null}
        <TextInput label="Source" description="Where the file came from." value={source}
                   onChange={(e) => setSource(e.currentTarget.value)} className="ip-source" />
        <TextInput label="Date the facts were true" description="Blank means today." type="date" value={asOf}
                   onChange={(e) => setAsOf(e.currentTarget.value)} className="ip-date" />
      </div>
      {!loadable ? <p className="muted">Load it needs a column read as a name or an address.</p> : null}
      {edited && !busy ? <p className="muted">The columns changed. Check again to see what loading it would do now, or load it as changed.</p> : null}
      {said ? <p className="refusal" role="alert">{said}</p> : null}

      {read.preview ? (
        <section className="ip-look" aria-label="Rows to look at">
          <h3>
            Rows to look at <span className="muted ui-section-count">({read.look.total.toLocaleString()})</span>
            {job.id && job.artifact_key ? <span className="ip-download"><DownloadJobFile token={token} jobId={job.id} /></span> : null}
          </h3>
          {read.look.total === 0 ? (
            <p className="muted">Every row matched a record or makes a new one.</p>
          ) : (
            <>
              <DataTable columns={lookSpecs} rows={read.look.rows} params={lookParams} rowKey={(r) => String(r.row)} />
              {read.look.total > LOOK_CAP ? <p className="muted">Showing the first {LOOK_CAP} of {read.look.total.toLocaleString()}. The download has every row.</p> : null}
            </>
          )}
        </section>
      ) : null}

      <section className="ip-columns" aria-label="Columns">
        <h3>
          <Button kind="link" onClick={() => setColumnsOpen(!columnsOpen)} aria-expanded={columnsOpen}>
            {columnsOpen ? "Hide the columns" : `Columns (${read.proposal.length})`}
          </Button>
          {!columnsOpen && needsALook ? <Badge tone="waiting">Some need a look</Badge> : null}
        </h3>
        {columnsOpen ? (
          <>
            <DataTable columns={columnSpecs} rows={read.proposal} params={columnParams} rowKey={(p) => p.header} />
            {constants.length ? (
              <div className="ip-constants">
                <span className="field-label">Added to every row</span>
                {constants.map((c) => (
                  <span key={c.field} className="ip-constant">
                    {constantWords(c, fields)} <span className="muted">({c.reason})</span>{" "}
                    <Button kind="link" size="compact-xs" onClick={() => setConstants(constants.filter((x) => x.field !== c.field))}>Remove</Button>
                  </span>
                ))}
              </div>
            ) : null}
          </>
        ) : null}
      </section>
    </section>
  );
}

/** The meanings a column can have, grouped as the engine lists them. */
function selectOptions(fields: readonly ImportField[]) {
  const groups = new Map<string, { value: string; label: string }[]>();
  for (const f of [...fields].sort((a, b) => (a.position ?? 0) - (b.position ?? 0))) {
    if (!f.target || !f.label) continue;
    const kind = f.kind ?? "other";
    if (!groups.has(kind)) groups.set(kind, []);
    groups.get(kind)?.push({ value: f.target, label: f.label });
  }
  return [...groups.entries()].map(([kind, items]) => ({ group: KIND_WORDS[kind] ?? kind, items }));
}

function constantWords(c: Constant, fields: readonly ImportField[]): string {
  const label = fields.find((f) => f.target === c.field)?.label ?? c.field;
  return `${label}: ${c.value}`;
}
